import { Slot } from '@radix-ui/react-slot';
import { cn } from '@thinkalpha/ui-core/utils';
import { forwardRef } from 'react';

export interface FloatingPaneProps extends React.HTMLAttributes<HTMLDivElement> {
    asChild?: boolean;
}

export const FloatingPane = forwardRef<HTMLDivElement, FloatingPaneProps>(
    ({ children, className, asChild, ...restProps }, ref) => {
        const Comp = asChild ? Slot : 'div';
        return (
            <Comp
                {...restProps}
                ref={ref}
                className={cn(
                    'bg-gray-50 text-gray-925 dark:bg-gray-925 dark:text-gray-50',
                    // z-sky-high so floating UI (dropdowns, tooltips, completion panes)
                    // renders above dialogs (z-high) — otherwise panes spawned from
                    // inside a dialog are hidden by the dialog surface.
                    'z-sky-high overflow-hidden rounded-lg border border-gray-700 shadow-md',
                    'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
                    className,
                )}
            >
                {children}
            </Comp>
        );
    },
);
FloatingPane.displayName = 'FloatingPane';

export const FloatingPaneSeparator = ({ className }: { className?: string }) => {
    return <div className={cn('-mx-1 my-1 h-px bg-gray-700', className)} />;
};

export interface FloatingPaneItemProps extends React.HTMLAttributes<HTMLDivElement> {
    asChild?: boolean;
}

export const FloatingPaneMenuItem = forwardRef<HTMLDivElement, FloatingPaneItemProps>(
    ({ children, className, asChild, ...restProps }, ref) => {
        const Comp = asChild ? Slot : 'div';
        return (
            <Comp
                {...restProps}
                ref={ref}
                className={cn(
                    'group/floating-pane-menu-item',
                    'text-md hover:bg-gray-800 focus:bg-gray-800 focus:text-accent-foreground data-highlighted:bg-gray-800',
                    'relative flex cursor-default items-center rounded-xs px-2 py-1.5 outline-hidden select-none data-[disabled]:opacity-50',
                    className,
                )}
            >
                {children}
            </Comp>
        );
    },
);
FloatingPaneMenuItem.displayName = 'FloatingPaneMenuItem';
